import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { setCurrentUser } from '../../ducks/user.duck';
import { logout } from '../../ducks/auth.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { deleteUserAccount } from '../../util/api';
import { executeFileUpload } from '../../util/fileHelpers';
import { types as sdkTypes } from '../../util/sdkLoader';
import { DOCUMENTS_PRIVATE_DATA_KEY } from '../../config/configProfileDocuments';

const { UUID } = sdkTypes;

// Attachment ids already arrive as UUID instances on currentUser, so they are
// passed straight back; only slot values (stored as plain strings in
// privateData, which has to stay JSON-serialisable) need rebuilding.
const toUUID = id => (id?.uuid ? id : new UUID(id));

const attachedFileIds = currentUser =>
  (currentUser?.privateFileAttachments || [])
    .filter(a => a.attributes?.deleted !== true)
    .map(a => a.file?.id)
    .filter(id => !!id?.uuid);

// Profile updates must ask for the file attachments back. mergeCurrentUser in
// user.duck.js shallow-merges relationships and keeps whatever the response
// omits, so an update that doesn't include privateFileAttachments would leave
// a stale list in the store and the panel would render the pre-save state.
const CURRENT_USER_QUERY_PARAMS = {
  expand: true,
  include: ['profileImage', 'privateFileAttachments', 'privateFileAttachments.file'],
};

const MINUTE_IN_MS = 60 * 1000;
const POLL_MAX_ATTEMPTS = 30;
const POLL_INTERVAL_MS = 1000;
const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
const getNextPollingDelay = delayMs =>
  delayMs * 2 > MINUTE_IN_MS ? MINUTE_IN_MS : delayMs * 2;

// ================ Async Thunks ================ //

////////////////////
// Delete Account //
////////////////////

export const deleteAccountThunk = createAsyncThunk(
  'ManageAccountPage/deleteAccount',
  (currentPassword, { dispatch, rejectWithValue }) => {
    return deleteUserAccount({ currentPassword })
      .then(() => {
        dispatch(markAccountDeleted());
        return dispatch(logout());
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);

// Backward compatible wrapper for the deleteAccount thunk
export const deleteAccount = actionPayload => dispatch => {
  return dispatch(deleteAccountThunk(actionPayload)).unwrap();
};

////////////////////
// Reset Password //
////////////////////

export const resetPasswordThunk = createAsyncThunk(
  'ManageAccountPage/resetPassword',
  (email, { extra: sdk, rejectWithValue }) => {
    return sdk.passwordReset.request({ email }).catch(e => {
      return rejectWithValue(storableError(e));
    });
  }
);

// Backward compatible wrapper for the resetPassword thunk
// Note: we unwrap the thunk so that the promise chain can be listened on presentational components.
export const resetPassword = actionPayload => dispatch => {
  return dispatch(resetPasswordThunk(actionPayload)).unwrap();
};

////////////////////
// Update Profile //
////////////////////

export const updateProfileThunk = createAsyncThunk(
  'ManageAccountPage/updateProfile',
  (actionPayload, { dispatch, rejectWithValue, extra: sdk }) => {
    const queryParams = CURRENT_USER_QUERY_PARAMS;

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(setCurrentUser(currentUser));
        return response;
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);

// Backward compatible wrapper for the updateProfile thunk
export const updateProfile = actionPayload => dispatch => {
  return dispatch(updateProfileThunk(actionPayload));
};

/////////////////////////
// Profile documents   //
/////////////////////////

// Vetting documents are attached to the user as privateFileAttachments. The
// upload half of this mirrors EditListingPage.duck.js (and TransactionPage.duck.js,
// which already made the same copy) — the only real differences are the redux
// state path and that the finished fileId is attached to the current user
// instead of to a listing.

const pollForFileVerificationPayloadCreator = (
  { fileId, tempId },
  { dispatch, rejectWithValue, extra: sdk, getState }
) => {
  const poll = (attempt, delayMs) => {
    // The user may have removed the row while verification was still running.
    const fileExists = getState().ManageAccountPage.fileUploads[tempId];
    if (!fileExists) {
      return { tempId, sent: true };
    }

    return sdk.ownFiles.show({ id: fileId }).then(resp => {
      const fileState = resp?.data?.data?.attributes?.state;
      dispatch(setVerificationStatus({ tempId, verificationStatus: fileState }));

      if (fileState === 'available') {
        const [fileUpload] = denormalisedResponseEntities(resp);
        return { fileUpload, tempId };
      }
      if (fileState === 'verificationFailed') {
        const [fileUpload] = denormalisedResponseEntities(resp);
        return rejectWithValue({ tempId, message: 'verificationFailed', fileUpload });
      }
      if (attempt >= POLL_MAX_ATTEMPTS - 1) {
        return rejectWithValue({ tempId, message: 'timeout' });
      }
      return delay(delayMs).then(() => poll(attempt + 1, getNextPollingDelay(delayMs)));
    });
  };

  return poll(0, POLL_INTERVAL_MS).catch(e => rejectWithValue({ tempId, error: storableError(e) }));
};

export const pollForFileVerificationThunk = createAsyncThunk(
  'ManageAccountPage/pollForFileVerification',
  pollForFileVerificationPayloadCreator
);

const uploadDocumentPayloadCreator = (
  { file, tempId, documentKey },
  { dispatch, rejectWithValue, extra: sdk, getState }
) => {
  return executeFileUpload({
    file,
    tempId,
    sdk,
    fileUploadCount: Object.keys(getState().ManageAccountPage.fileUploads).length,
    onProgress: progress => dispatch(setUploadProgress({ tempId, progress })),
  })
    .then(resp => {
      // Verification runs in the background; attaching waits for it (see
      // attachDocumentThunk, dispatched once polling reports 'available').
      dispatch(pollForFileVerificationThunk({ fileId: resp.fileId, tempId })).then(pollResult => {
        if (pollForFileVerificationThunk.fulfilled.match(pollResult)) {
          dispatch(attachDocumentThunk({ fileId: resp.fileId, documentKey }));
        }
      });
      return { ...resp, documentKey };
    })
    .catch(e => rejectWithValue({ tempId, error: storableError(e) }));
};

export const uploadDocumentThunk = createAsyncThunk(
  'ManageAccountPage/uploadDocument',
  uploadDocumentPayloadCreator
);

export const uploadDocument = (file, tempId, documentKey) => dispatch => {
  return dispatch(uploadDocumentThunk({ file, tempId, documentKey }));
};

/**
 * Attach a verified file to the current user.
 *
 * Two things make this more than a one-liner:
 *  - privateFileAttachments REPLACES the whole list on every update, so the
 *    existing attachments have to be read off currentUser and sent back.
 *  - the attachment list is flat and carries no slot label, so the
 *    documentKey -> fileId map is written to privateData alongside it. Filling
 *    a slot that already holds a file drops the old attachment.
 */
// Slot writes are serialised through this queue. attachDocument and
// removeDocument each read the current documentSlots off the store and send the
// WHOLE map back — update_profile merges top-level keys only, so the map is
// replaced, never merged. When several uploads finish scanning in the same
// window (the normal case for a member who picks their 4-7 files in a row)
// every write read the same pre-write map and the last one won: three of four
// documents vanished on the dev deployment on 2026-09-11 (four update_profile
// calls back-to-back, one slot persisted). Queueing means each write reads the
// map the previous write has just stored via setCurrentUser.
let slotWriteQueue = Promise.resolve();
const enqueueSlotWrite = work => {
  const run = slotWriteQueue.then(work, work);
  // A failed write must not jam the queue for the next one.
  slotWriteQueue = run.catch(() => null);
  return run;
};

const attachDocumentPayloadCreator = ({ fileId, documentKey }, thunkApi) =>
  enqueueSlotWrite(() => attachDocumentNow({ fileId, documentKey }, thunkApi));

const attachDocumentNow = (
  { fileId, documentKey },
  { dispatch, rejectWithValue, extra: sdk, getState }
) => {
  const currentUser = getState().user.currentUser;
  const privateData = currentUser?.attributes?.profile?.privateData || {};
  const slots = privateData[DOCUMENTS_PRIVATE_DATA_KEY] || {};

  const newFileId = toUUID(fileId);
  const replacedFileUuid = slots[documentKey];

  const kept = attachedFileIds(currentUser).filter(
    id => id.uuid !== replacedFileUuid && id.uuid !== newFileId.uuid
  );

  const privateFileAttachments = [...kept, newFileId].map(id => ({ fileId: id }));

  return sdk.currentUser
    .updateProfile(
      {
        privateFileAttachments,
        privateData: {
          [DOCUMENTS_PRIVATE_DATA_KEY]: { ...slots, [documentKey]: newFileId.uuid },
        },
      },
      CURRENT_USER_QUERY_PARAMS
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      dispatch(setCurrentUser(entities[0]));
      return { documentKey, fileId: newFileId.uuid };
    })
    .catch(e => rejectWithValue({ documentKey, error: storableError(e) }));
};

export const attachDocumentThunk = createAsyncThunk(
  'ManageAccountPage/attachDocument',
  attachDocumentPayloadCreator
);

/** Detach a document: drop its fileId from the list and clear its slot. */
const removeDocumentPayloadCreator = ({ documentKey }, thunkApi) =>
  enqueueSlotWrite(() => removeDocumentNow({ documentKey }, thunkApi));

const removeDocumentNow = (
  { documentKey },
  { dispatch, rejectWithValue, extra: sdk, getState }
) => {
  const currentUser = getState().user.currentUser;
  const privateData = currentUser?.attributes?.profile?.privateData || {};
  const slots = privateData[DOCUMENTS_PRIVATE_DATA_KEY] || {};
  const removedFileUuid = slots[documentKey];

  const privateFileAttachments = attachedFileIds(currentUser)
    .filter(id => id.uuid !== removedFileUuid)
    .map(id => ({ fileId: id }));

  const nextSlots = { ...slots };
  delete nextSlots[documentKey];

  return sdk.currentUser
    .updateProfile(
      { privateFileAttachments, privateData: { [DOCUMENTS_PRIVATE_DATA_KEY]: nextSlots } },
      CURRENT_USER_QUERY_PARAMS
    )
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      dispatch(setCurrentUser(entities[0]));
      return { documentKey };
    })
    .catch(e => rejectWithValue({ documentKey, error: storableError(e) }));
};

export const removeDocumentThunk = createAsyncThunk(
  'ManageAccountPage/removeDocument',
  removeDocumentPayloadCreator
);

export const removeDocument = documentKey => dispatch => {
  return dispatch(removeDocumentThunk({ documentKey }));
};

const downloadDocumentPayloadCreator = ({ fileId }, { rejectWithValue, extra: sdk }) => {
  if (!fileId) {
    throw new Error('Missing fileId, cannot initiate download.');
  }
  return sdk.ownFileDownloads
    .create({ fileId: toUUID(fileId) })
    .then(downloadResp => {
      const { url } = downloadResp?.data?.data?.attributes || {};
      if (!url) {
        throw new Error('Missing download URL, cannot trigger file download.');
      }
      window.open(url, '_blank', 'noopener,noreferrer');
      return fileId;
    })
    .catch(e => rejectWithValue({ fileId, error: storableError(e) }));
};

export const downloadDocumentThunk = createAsyncThunk(
  'ManageAccountPage/downloadDocument',
  downloadDocumentPayloadCreator
);

export const downloadDocument = fileId => dispatch => {
  return dispatch(downloadDocumentThunk({ fileId }));
};

// ================ Slice ================ //

const manageAccountPageSlice = createSlice({
  name: 'ManageAccountPage',
  initialState: {
    deleteAccountError: null,
    deleteAccountInProgress: false,
    accountDeletionConfirmed: false,
    accountMarkedDeleted: false,
    resetPasswordInProgress: false,
    resetPasswordError: null,
    updateProfileInProgress: false,
    updateProfileError: null,
    // [tempId]: { tempId, documentKey, uploadInProgress, verificationInProgress,
    //             progress, verificationStatus, file, sourceFile, error }
    fileUploads: {},
    fileUploadsDisabled: false,
    // [documentKey]: { inProgress, error }
    documentActions: {},
  },
  reducers: {
    markAccountDeleted: state => {
      state.accountMarkedDeleted = true;
    },
    clearUploadedFiles: (state, action) => {
      const tempIds = action.payload || [];
      tempIds.forEach(tempId => {
        delete state.fileUploads[tempId];
      });
    },
    setFileUploadsDisabled: state => {
      state.fileUploadsDisabled = true;
    },
    setUploadProgress: (state, action) => {
      const { tempId, progress } = action.payload;
      if (state.fileUploads[tempId]) {
        state.fileUploads[tempId].progress = progress;
      }
    },
    setVerificationStatus: (state, action) => {
      const { tempId, verificationStatus } = action.payload;
      if (state.fileUploads[tempId]) {
        state.fileUploads[tempId].verificationStatus = verificationStatus;
        state.fileUploads[tempId].uploadInProgress = verificationStatus === 'pendingUpload';
        state.fileUploads[tempId].verificationInProgress =
          verificationStatus === 'pendingVerification';
      }
    },
  },
  extraReducers: builder => {
    builder
      // deleteAccount cases
      .addCase(deleteAccountThunk.pending, state => {
        state.deleteAccountInProgress = true;
        state.accountDeletionConfirmed = false;
      })
      .addCase(deleteAccountThunk.fulfilled, state => {
        state.deleteAccountInProgress = false;
        state.accountDeletionConfirmed = true;
      })
      .addCase(deleteAccountThunk.rejected, (state, action) => {
        state.deleteAccountInProgress = false;
        state.deleteAccountError = action.payload;
      })
      // resetPassword cases
      .addCase(resetPasswordThunk.pending, state => {
        state.resetPasswordInProgress = true;
        state.resetPasswordError = null;
      })
      .addCase(resetPasswordThunk.fulfilled, state => {
        state.resetPasswordInProgress = false;
      })
      .addCase(resetPasswordThunk.rejected, (state, action) => {
        state.resetPasswordInProgress = false;
        state.resetPasswordError = action.payload;
      })
      // updateProfile cases
      .addCase(updateProfileThunk.pending, state => {
        state.updateProfileInProgress = true;
        state.updateProfileError = null;
      })
      .addCase(updateProfileThunk.fulfilled, state => {
        state.updateProfileInProgress = false;
      })
      .addCase(updateProfileThunk.rejected, (state, action) => {
        state.updateProfileInProgress = false;
        state.updateProfileError = action.payload;
      })
      // uploadDocument cases
      .addCase(uploadDocumentThunk.pending, (state, action) => {
        const { tempId, file, documentKey } = action.meta.arg;
        state.fileUploads[tempId] = {
          tempId,
          documentKey,
          uploadInProgress: true,
          verificationInProgress: false,
          progress: null,
          verificationStatus: null,
          file: null,
          sourceFile: file,
          error: null,
        };
      })
      .addCase(uploadDocumentThunk.fulfilled, (state, action) => {
        const { tempId, fileUpload } = action.payload;
        if (state.fileUploads[tempId]) {
          state.fileUploads[tempId].file = fileUpload;
          state.fileUploads[tempId].sourceFile = null;
        }
      })
      .addCase(uploadDocumentThunk.rejected, (state, action) => {
        const { tempId, error } = action.payload || {};
        // The marketplace can switch uploads off at runtime; when it has, there
        // is nothing to retry and leaving half-finished rows on screen is worse
        // than clearing them.
        const isUploadDisabled =
          error?.status === 403 && error?.apiErrors?.some(ae => ae.code === 'file-upload-disabled');
        if (isUploadDisabled) {
          state.fileUploadsDisabled = true;
          state.fileUploads = {};
          return;
        }
        if (tempId && state.fileUploads[tempId]) {
          state.fileUploads[tempId].uploadInProgress = false;
          state.fileUploads[tempId].verificationInProgress = false;
          state.fileUploads[tempId].error = error || { message: 'uploadFailed' };
        }
      })
      // verification polling cases
      .addCase(pollForFileVerificationThunk.pending, (state, action) => {
        const { tempId } = action.meta.arg;
        if (state.fileUploads[tempId]) {
          state.fileUploads[tempId].verificationInProgress = true;
        }
      })
      .addCase(pollForFileVerificationThunk.fulfilled, (state, action) => {
        const { tempId, fileUpload } = action.payload || {};
        if (tempId && state.fileUploads[tempId]) {
          state.fileUploads[tempId].verificationInProgress = false;
          state.fileUploads[tempId].uploadInProgress = false;
          if (fileUpload) {
            state.fileUploads[tempId].file = fileUpload;
          }
        }
      })
      .addCase(pollForFileVerificationThunk.rejected, (state, action) => {
        const { tempId, message, error } = action.payload || {};
        if (tempId && state.fileUploads[tempId]) {
          state.fileUploads[tempId].verificationInProgress = false;
          state.fileUploads[tempId].uploadInProgress = false;
          state.fileUploads[tempId].error = error || { message: message || 'verificationFailed' };
        }
      })
      // attach / remove cases, keyed by document slot
      .addCase(attachDocumentThunk.pending, (state, action) => {
        const { documentKey } = action.meta.arg;
        state.documentActions[documentKey] = { inProgress: true, error: null };
      })
      .addCase(attachDocumentThunk.fulfilled, (state, action) => {
        const { documentKey } = action.payload;
        state.documentActions[documentKey] = { inProgress: false, error: null };
        // The file now lives on currentUser, so the transient upload row for
        // this slot is redundant — drop it so the panel renders one entry.
        Object.keys(state.fileUploads).forEach(tempId => {
          if (state.fileUploads[tempId].documentKey === documentKey) {
            delete state.fileUploads[tempId];
          }
        });
      })
      .addCase(attachDocumentThunk.rejected, (state, action) => {
        const { documentKey, error } = action.payload || {};
        if (documentKey) {
          state.documentActions[documentKey] = { inProgress: false, error };
        }
      })
      .addCase(removeDocumentThunk.pending, (state, action) => {
        const { documentKey } = action.meta.arg;
        state.documentActions[documentKey] = { inProgress: true, error: null };
      })
      .addCase(removeDocumentThunk.fulfilled, (state, action) => {
        const { documentKey } = action.payload;
        state.documentActions[documentKey] = { inProgress: false, error: null };
      })
      .addCase(removeDocumentThunk.rejected, (state, action) => {
        const { documentKey, error } = action.payload || {};
        if (documentKey) {
          state.documentActions[documentKey] = { inProgress: false, error };
        }
      });
  },
});

export const {
  markAccountDeleted,
  clearUploadedFiles,
  setFileUploadsDisabled,
  setUploadProgress,
  setVerificationStatus,
} = manageAccountPageSlice.actions;

// ================ Selectors ================ //

export const selectFileUploads = state => Object.values(state.ManageAccountPage.fileUploads);
export const selectFileUploadsDisabled = state => state.ManageAccountPage.fileUploadsDisabled;
export const selectDocumentActions = state => state.ManageAccountPage.documentActions;
export const selectHasPendingFileUploads = state =>
  Object.values(state.ManageAccountPage.fileUploads).some(
    f => f.uploadInProgress || f.verificationInProgress
  );

export default manageAccountPageSlice.reducer;
